import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { OSHAR_REAL_ESTATE_SEO } from "../../constants/siteSeo";
import { usePageSeo } from "../../hooks/usePageSeo";
import RealEstateHero from "../../components/oshar/real-estate/RealEstateHero";
import CircularCityMap from "../../components/oshar/real-estate/CircularCityMap";
import PropertyTypeGrid from "../../components/oshar/real-estate/PropertyTypeGrid";
import GenesisReleasePanel from "../../components/oshar/real-estate/GenesisReleasePanel";
import FeaturedProperties from "../../components/oshar/real-estate/FeaturedProperties";
import HowItWorks from "../../components/oshar/real-estate/HowItWorks";
import WhyOwnOshar from "../../components/oshar/real-estate/WhyOwnOshar";
import RealEstateDisclosure from "../../components/oshar/real-estate/RealEstateDisclosure";
import RealEstateCTA from "../../components/oshar/real-estate/RealEstateCTA";
import RealEstateDetailModal from "../../components/oshar/real-estate/RealEstateDetailModal";
import AuthModal from "../../components/auth/AuthModal";
import {
  assetTypes as staticAssetTypes,
  districts,
  genesisPhaseOne,
  featuredListings,
  howItWorksSteps,
  whyOwnBenefits,
} from "../../data/osharRealEstate";
import { mapPublicOsharTierListToAssetTypes } from "../../lib/osharRealEstateTierDtoMap";
import { getOsharRealEstateTiers, getOsharRealEstateSalesReadiness } from "../../services/osharRealEstateTiersApi";
import { handleViewDetails } from "../../lib/osharRealEstatePlaceholders";
import { trackOsharRealEstateView } from "../../utils/analytics";
import { trackProductViewed } from "../../services/analyticsClient";
import "./oshar-page.css";
import "./oshar-real-estate.css";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

function applyStripeFulfillmentFlags(baseList, readinessPayload) {
  const m = new Map();
  for (const t of readinessPayload?.tiers || []) {
    if (t && t.tierSlug) m.set(String(t.tierSlug).trim(), t.fulfillmentReady === true);
  }
  return (baseList || []).map((a) => {
    const slug = String(a?.tierSlug || a?.id || "").trim();
    const stripeFulfillmentReady = slug ? m.get(slug) === true : false;
    return { ...a, stripeFulfillmentReady };
  });
}

const OsharRealEstatePage = () => {
  usePageSeo(OSHAR_REAL_ESTATE_SEO);
  const location = useLocation();
  const [districtId, setDistrictId] = useState(districts[0].id);
  const [assetTypes, setAssetTypes] = useState(staticAssetTypes);
  /** "loading" until GET /tiers resolves; "api" when canonical tiers loaded; "fallback" when the request failed or returned no tiers. */
  const [tierSource, setTierSource] = useState("loading");
  /** Latest GET /api/oshar-real-estate/tiers outcome (for debug panel; not secrets). */
  const [tiersHttpMeta, setTiersHttpMeta] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMessage, setAuthModalMessage] = useState("");
  const isCheckoutCancelled = new URLSearchParams(location.search).get("reservation") === "cancelled";

  /** Prefer backend canonical draft tiers; keep bundled osharRealEstate.js if API fails. */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { tiers, tiersRequest } = await getOsharRealEstateTiers();
        if (cancelled) return;
        setTiersHttpMeta(tiersRequest || null);
        if (!tiers.length) {
          setAssetTypes(staticAssetTypes);
          setTierSource("fallback");
          return;
        }
        const mapped = mapPublicOsharTierListToAssetTypes(tiers);
        if (mapped.length) {
          setAssetTypes(mapped);
          setTierSource("api");
        } else {
          setAssetTypes(staticAssetTypes);
          setTierSource("fallback");
        }
      } catch {
        if (cancelled) return;
        setAssetTypes(staticAssetTypes);
        setTierSource("fallback");
      }
    })();

    // Optional Stripe fulfillment flags — must never block live /tiers catalog rendering.
    void getOsharRealEstateSalesReadiness().then((readiness) => {
      if (cancelled) return;
      setAssetTypes((prev) => applyStripeFulfillmentFlags(prev, readiness));
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    trackProductViewed("oshar_real_estate", "page", { view: "catalog" });
  }, []);

  const openDetails = useCallback(
    (item) => {
      handleViewDetails(item);
      const slug = String(item?.tierSlug || item?.id || "").trim();
      const fromCatalog = slug ? assetTypes.find((a) => String(a?.tierSlug || a?.id || "").trim() === slug) : null;
      /** Prefer backend-mapped inventory/pricing when the grid/catalog has this tier (fixes featured cards missing `remaining` / wrong `checkoutEnabled`). */
      const merged =
        fromCatalog && slug
          ? {
              ...item,
              ...fromCatalog,
              title: item?.title || fromCatalog.name,
              name: item?.name || fromCatalog.name,
              description: item?.description ?? fromCatalog.description,
              shortDescription: item?.shortDescription ?? fromCatalog.shortDescription,
              imageKey: item?.imageKey ?? fromCatalog.imageKey,
              utilityList: Array.isArray(item?.utilityList) && item.utilityList.length ? item.utilityList : fromCatalog.utilityList,
            }
          : item;
      setDetailItem(merged);
      if (slug) {
        trackProductViewed("oshar_property", slug, {
          title: merged?.title || merged?.name || undefined,
        });
      }
    },
    [assetTypes]
  );

  /** When `/tiers` loads after the modal opened, refresh `detailItem` so `checkoutEnabled` / `remaining` stay accurate. */
  useEffect(() => {
    setDetailItem((prev) => {
      if (!prev) return prev;
      const slug = String(prev.tierSlug || prev.id || "").trim();
      const fromCatalog = slug ? assetTypes.find((a) => String(a?.tierSlug || a?.id || "").trim() === slug) : null;
      if (!fromCatalog) return prev;
      return {
        ...prev,
        ...fromCatalog,
        title: prev.title || fromCatalog.name,
        name: prev.name || fromCatalog.name,
        description: prev.description ?? fromCatalog.description,
        shortDescription: prev.shortDescription ?? fromCatalog.shortDescription,
        imageKey: prev.imageKey ?? fromCatalog.imageKey,
        utilityList: Array.isArray(prev.utilityList) && prev.utilityList.length ? prev.utilityList : fromCatalog.utilityList,
      };
    });
  }, [assetTypes]);

  const onRequireAuthForReserve = useCallback(() => {
    setAuthModalMessage(
      "Log in or create a Treydora account to pay with card (Stripe) for Oshar properties and view access on your dashboard."
    );
    setAuthModalOpen(true);
  }, []);

  const trackAndScroll = useCallback((ctaId, targetId) => {
    trackOsharRealEstateView({
      cta_id: ctaId,
      section: "oshar_real_estate",
      destination: `#${targetId}`,
    });
    scrollToId(targetId);
  }, []);

  return (
    <div className="oshar-page oshar-re-page">
      <main>
        {isCheckoutCancelled && (
          <section className="oshar-inner" style={{ paddingTop: 24 }}>
            <div
              style={{
                border: "1px solid rgba(201,165,74,0.35)",
                background: "rgba(8,14,30,0.88)",
                color: "#d4dfef",
                padding: "12px 14px",
              }}
            >
              Checkout cancelled. No payment was completed.
            </div>
          </section>
        )}
        <RealEstateHero
          onBrowseProperties={() => trackAndScroll("real_estate_hero_browse_properties", "oshar-re-property-types")}
          onHowItWorks={() => trackAndScroll("real_estate_hero_how_it_works", "oshar-re-how-it-works")}
        />
        <CircularCityMap
          districts={districts}
          selectedId={districtId}
          onSelectDistrict={setDistrictId}
          onViewProperties={() => trackAndScroll("real_estate_map_view_properties", "oshar-re-property-types")}
        />
        <PropertyTypeGrid
          assetTypes={assetTypes}
          tierSource={tierSource}
          tiersHttpMeta={tiersHttpMeta}
          onAuthRequired={onRequireAuthForReserve}
          scrollTargetId="oshar-re-property-types"
        />
        <GenesisReleasePanel genesis={genesisPhaseOne} />
        <FeaturedProperties listings={featuredListings} onViewDetails={openDetails} />
        <HowItWorks steps={howItWorksSteps} />
        <WhyOwnOshar benefits={whyOwnBenefits} />
        <RealEstateDisclosure />
        <RealEstateCTA
          onBrowseAll={() => trackAndScroll("real_estate_final_browse_all", "oshar-re-property-types")}
          onLearnOwnership={() => trackAndScroll("real_estate_final_learn_ownership", "oshar-re-how-it-works")}
        />
      </main>
      {detailItem && (
        <RealEstateDetailModal
          item={detailItem}
          onClose={() => setDetailItem(null)}
          onAuthRequired={onRequireAuthForReserve}
        />
      )}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialMessage={authModalMessage}
        loginSuccessMessage="You're signed in. Continue with Pay with Card or Mint with Crypto Wallet."
        registerSuccessMessage="Account created. Continue with Pay with Card or Mint with Crypto Wallet."
      />
    </div>
  );
};

export default OsharRealEstatePage;
